/**
 * The Fixed Income tab, at three granularities.
 *
 * The perspectives do NOT read the same thing, and that is the point:
 *
 *   Monthly  the recon itself — Clearwater against the custodian files, at
 *            month-end. Every custodian feed (custodians.holdings_*, recon.psn_*)
 *            is a month-end file, so this is the only granularity at which a
 *            two-sided reconciliation exists. Unchanged from what this tab has
 *            always shown.
 *   Weekly   Clearwater's daily positions at each week's close, against the
 *   Daily    prior week's close — or a single business day against the previous
 *            one. One-sided, because there is no custodian file to compare
 *            against mid-month.
 *
 * Only the selected perspective is mounted, so leaving Monthly discards whatever
 * the recon grid was holding. Comments live in that grid until they are saved, so
 * switching away with unsaved ones asks first rather than dropping them silently.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ConfirmationDialog } from '@1823-partners/core';

import ReconList from '../ReconList';
import DailyPositions from './DailyPositions';
import { GRANULARITIES, GRANULARITY_LABELS } from '../../utils/dailyPeriods';

const PERSPECTIVE_NOTES = {
  monthly: 'Month-end recon: Clearwater against the custodian holdings files.',
  weekly: 'Clearwater daily positions at each week close, against the prior week.',
  daily: 'Clearwater daily positions, against the previous business day.',
};

// The toggle row costs vertical space the grid would otherwise have.
const HEADER_HEIGHT = 56;

const FixedIncomeRecon = ({
  userId, height = 700, onSelectEntry, onSaveSuccess, onExclusionSuccess, onUnsavedChange,
}) => {
  const [granularity, setGranularity] = useState('monthly');
  const [monthlyDirty, setMonthlyDirty] = useState(false);

  // The Dashboard's tab switch unmounts this whole tab, so it needs to know too.
  useEffect(() => {
    onUnsavedChange?.(granularity === 'monthly' && monthlyDirty);
  }, [granularity, monthlyDirty, onUnsavedChange]);
  // The perspective the user asked for while the recon grid had unsaved comments.
  const [pendingGranularity, setPendingGranularity] = useState(null);

  const handleGranularity = useCallback((_event, value) => {
    // ToggleButtonGroup reports null when the active button is clicked again;
    // there is no "no perspective" state, so that is a no-op.
    if (!value || value === granularity) return;
    if (granularity === 'monthly' && monthlyDirty) {
      setPendingGranularity(value);
      return;
    }
    setGranularity(value);
  }, [granularity, monthlyDirty]);

  const discardAndSwitch = useCallback(() => {
    setGranularity(pendingGranularity);
    setPendingGranularity(null);
    setMonthlyDirty(false);
  }, [pendingGranularity]);

  const contentHeight = Math.max(height - HEADER_HEIGHT, 240);

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={granularity}
          onChange={handleGranularity}
          aria-label="Fixed Income granularity"
        >
          {GRANULARITIES.map((value) => (
            <ToggleButton
              key={value}
              value={value}
              aria-label={`${GRANULARITY_LABELS[value]} view`}
              sx={{ textTransform: 'none', fontWeight: 700, px: 2 }}
            >
              {GRANULARITY_LABELS[value]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {PERSPECTIVE_NOTES[granularity]}
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {granularity === 'monthly' ? (
          <ReconList
            onSelectEntry={onSelectEntry}
            onSaveSuccess={onSaveSuccess}
            onExclusionSuccess={onExclusionSuccess}
            userId={userId}
            height={contentHeight}
            onUnsavedChange={setMonthlyDirty}
          />
        ) : (
          <DailyPositions granularity={granularity} height={contentHeight} />
        )}
      </Box>

      <ConfirmationDialog
        open={pendingGranularity !== null}
        onClose={() => setPendingGranularity(null)}
        onConfirm={discardAndSwitch}
        title="Discard unsaved comments?"
        confirmText="Discard and switch"
        confirmColor="error"
        message={
          `The month-end recon is holding comments that have not been saved. Switching to the `
          + `${GRANULARITY_LABELS[pendingGranularity] || ''} view discards them.`
        }
      />
    </Box>
  );
};

export default FixedIncomeRecon;
